// The cells the terrain's Retângulo and Preencher tools paint. Both keep to
// the paintable cells, as a brush stroke does.
import { EMPTY, MAX_CELLS, paintable } from "./terrain";

type Cell = { col: number; row: number };
type Point = { x: number; y: number };
type GridMath = {
	grid: { kind: string };
	cellAt: (x: number, y: number) => Cell;
	centerOf: (col: number, row: number) => Point;
	distance: (from: Cell, to: Cell) => number;
};
type Scene = { width_px: number; height_px: number };

// Every paintable cell of the rectangle the two points' cells span, whichever
// way it was dragged; on hexes, the block of columns and rows between them.
export function rectCells(math: GridMath, scene: Scene, from: Point, to: Point): Cell[] {
	const a = math.cellAt(from.x, from.y);
	const b = math.cellAt(to.x, to.y);
	const [col0, col1] = [Math.max(0, Math.min(a.col, b.col)), Math.min(MAX_CELLS - 1, Math.max(a.col, b.col))];
	const [row0, row1] = [Math.max(0, Math.min(a.row, b.row)), Math.min(MAX_CELLS - 1, Math.max(a.row, b.row))];
	const cells: Cell[] = [];
	for (let row = row0; row <= row1; row++) {
		for (let col = col0; col <= col1; col++) if (paintable(math, scene, { col, row })) cells.push({ col, row });
	}
	return cells;
}

// The paintable cells joined to the start through neighbours that share its
// code, uncarved being one code; none when the start is not paintable.
export function fillCells(math: GridMath, scene: Scene, rows: string[], start: Cell): Cell[] {
	const codeAt = (col: number, row: number) => rows[row]?.[col] ?? EMPTY;
	if (!paintable(math, scene, start)) return [];
	const code = codeAt(start.col, start.row);
	const seen = new Set([start.row * MAX_CELLS + start.col]);
	const cells: Cell[] = [];
	const queue = [start];
	for (let cell = queue.pop(); cell; cell = queue.pop()) {
		cells.push(cell);
		for (const next of neighbours(math, cell)) {
			const key = next.row * MAX_CELLS + next.col;
			if (seen.has(key) || !paintable(math, scene, next) || codeAt(next.col, next.row) !== code) continue;
			seen.add(key);
			queue.push(next);
		}
	}
	return cells;
}

const SIDES = [
	[1, 0],
	[-1, 0],
	[0, 1],
	[0, -1],
];
const CORNERS = [
	[1, 1],
	[1, -1],
	[-1, 1],
	[-1, -1],
];

// A square's four sides; a hex's six, two of them corner steps that depend on its column's shift.
function neighbours(math: GridMath, { col, row }: Cell): Cell[] {
	const step = ([dc, dr]: number[]) => ({ col: col + dc, row: row + dr });
	const sides = SIDES.map(step);
	if (math.grid.kind !== "hex") return sides;
	return [...sides, ...CORNERS.map(step).filter((next) => math.distance({ col, row }, next) === 1)];
}
